import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import { IntakeSectionHeader, StyledGridRow } from "@/common/styles";
import DynamicForm from "@/common/DynamicForm";
import { staticFields } from "@/util/common_variable";
import { PERSON } from "../InmateDetails/Personal";
import { getVisibleFields, isNonEmptyVariable } from "@/util/common_function";
import DetentionAliasesList from "./DetentionAliasesList";
import PropTypes from "prop-types";

const DETENTION_BIO_FIELD_NAMES = [
  "lName",
  "fName",
  "mName",
  "suffix",
  "ssn",
  "dob",
  "race",
  "sex",
  "height",
  "weight",
  "eyes",
  "ethnicity",
  "dlNumber",
  "dlState",
  "dlExpiration",
];

const bookingsBioFields = getVisibleFields(
  staticFields.personalInfo,
  null,
  PERSON.BOOKINGS
);

const detentionBioDynamicFields = [
  ...DETENTION_BIO_FIELD_NAMES.map((columnName) =>
    bookingsBioFields.find(
      ({ columnName: fieldColumnName }) => fieldColumnName === columnName
    )
  )
    .filter(Boolean)
    .map((field) => ({ ...field })),
      {
    columnName: "sid",
    type: "varchar(100)",
    tableName: "Bio",
    label: "SID",
  },
  {
    columnName: "fbi",
    type: "varchar(100)",
    tableName: "Bio",
    label: "FBI",
  },
  {
    columnName: "placeOfBirthCountry",
    type: "varchar(100)",
    tableName: "Bio",
    label: "Country of Birth",
  },
  {
    columnName: "streetNumber",
    type: "varchar(20)",
    tableName: "Bio",
    label: "#",
  },
  {
    columnName: "street1",
    type: "varchar(200)",
    tableName: "Bio",
    label: "Street",
  },
  {
    columnName: "street2",
    type: "varchar(200)",
    tableName: "Bio",
    label: "Street 2",
  },
  {
    columnName: "apartmentNumber",
    type: "varchar(50)",
    tableName: "Bio",
    label: "Apt #",
  },
  {
    columnName: "city",
    type: "varchar(100)",
    tableName: "Bio",
    label: "City",
  },
  {
    refListName: "USState",
    columnName: "state",
    type: "bit",
    tableName: "Bio",
    label: "ST",
    isID: false,
  },
  {
    columnName: "zip",
    type: "varchar(10)",
    tableName: "Bio",
    label: "Zip Code",
  }
];


const normalizeAliasForState = (alias = {}) => ({
  ...alias,
  questionedIdentity:
    typeof alias?.questionedIdentity === "string"
      ? alias.questionedIdentity.toLowerCase() === "true"
      : !!alias?.questionedIdentity,
});

const mapApiAliasesToState = (aliases = []) =>
  Array.isArray(aliases) ? aliases.map(normalizeAliasForState) : [];

const mapStateAliasesToApi = (aliases = []) =>
  Array.isArray(aliases)
    ? aliases.map(({ id, questionedIdentity, ...rest }) => ({
        ...rest,
        questionedIdentity:
          typeof questionedIdentity === "string"
            ? questionedIdentity.toLowerCase() === "true"
            : !!questionedIdentity,
      }))
    : [];

const buildFormValues = (bio = {}) => {
  if (!isNonEmptyVariable(bio) || Object.keys(bio).length === 0) return {};

  return {
    ...bio,
    dlNumber: bio?.dlNumber ?? bio?.dlnumber ?? null,
    dob: isNonEmptyVariable(bio?.dob) ? dayjs(bio.dob) : null,
    dlExpiration: isNonEmptyVariable(bio?.dlExpiration)
      ? dayjs(bio.dlExpiration)
      : null,
    placeOfBirthCountry: bio?.placeOfBirthCountry ?? null,
  };
};

const formatPayload = (values = {}, aliases = [], currentBio = {}) => {
  const payload = {
    ...currentBio,
    ...values,
    dob: values?.dob ? dayjs(values.dob).format("MM/DD/YYYY") : null,
    dlExpiration: values?.dlExpiration
      ? dayjs(values.dlExpiration).format("MM/DD/YYYY")
      : null,
    detentionsBioAliases: mapStateAliasesToApi(aliases),
  };

  if ("dlNumber" in payload) {
    payload.dlnumber = payload.dlNumber;
  }

  return payload;
};

const DetentionsBioForm = forwardRef(
  ({ bio = {}, personalFormRef, onUpdateForm = () => {}, disabled = false }, ref) => {
    const [form] = Form.useForm();
    const isSettingInitialValues = useRef(false);
    const [aliases, setAliases] = useState(
      mapApiAliasesToState(bio?.detentionsBioAliases || [])
    );

    useEffect(() => {
      if (isNonEmptyVariable(bio) && Object.keys(bio).length > 0) {
        isSettingInitialValues.current = true;
        form.setFieldsValue(buildFormValues(bio));
        setAliases(mapApiAliasesToState(bio?.detentionsBioAliases || []));
      } else {
        form.resetFields();
        setAliases([]);
      }
    }, [bio, form]);

    useImperativeHandle(ref || personalFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      validateFields: () => form.validateFields(),
      form,
    }));

    const emitUpdate = (values, updatedAliases = aliases) => {
      onUpdateForm(formatPayload(values, updatedAliases, bio));
    };

    const handleAliasesChange = (updatedAliases = []) => {
      setAliases(updatedAliases);
      emitUpdate(form.getFieldsValue(), updatedAliases);
    };

    return (
      <>
        <IntakeSectionHeader style={{ marginTop: "0px" }}>
          <span>Personal Information</span>
        </IntakeSectionHeader>

        <Form
          form={form}
          layout="vertical"
          disabled={disabled}
          onValuesChange={(_, allValues) => {
            if (isSettingInitialValues.current) {
              isSettingInitialValues.current = false;
              return;
            }
            emitUpdate(allValues);
          }}
          onBlur={() => emitUpdate(form.getFieldsValue())}
        >
          <StyledGridRow gutter={[16, 0]}>
            {detentionBioDynamicFields.map((field, index) => (
              <DynamicForm
                key={field.columnName || index}
                formAttributes={field}
                i={index}
                form={form}
                person={PERSON.BOOKINGS}
                bookingsFieldsEnabled={!disabled}
                view
                edit={!disabled}
              />
            ))}
          </StyledGridRow>

          <DetentionAliasesList
            aliases={aliases}
            disabled={disabled}
            onChange={handleAliasesChange}
          />
        </Form>
      </>
    );
  }
);
DetentionsBioForm.displayName = "DetentionsBioForm";

DetentionsBioForm.propTypes = {
  bio: PropTypes.object,
  personalFormRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  onUpdateForm: PropTypes.func,
  disabled: PropTypes.bool,
};

export default DetentionsBioForm;
